import WhatsappIcon from '@mui/icons-material/WhatsApp'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Icon from '@mui/material/Icon'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { styled } from '@mui/material/styles'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import React from 'react'

import Message from '@/app/component/send/message'
import { DEFAULT_SENDER_ID_VALUE, noop } from '@/app/definitions'
import { usePrevious } from '@/app/helpers'
import { ContentWhatsApp } from '@/app/module/campaigns/types'
import { useSendMessage } from '@/app/module/common/hooks/use-send-message'
import WhatsApp from '@/app/module/common/whatsapp'
import { shouldDisplayConfirmation, WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { SenderIdType, TGetFileFunction } from '@/app/module/conversations/types'
import { SendWhatsappMessagePayload } from '@/app/module/logs/types'
import { FilesType, FileType, FileUploadType, MessageType, PersonalizationType } from '@/app/types'

type Props = {
  contactId: number
  disabled: boolean
  messageType: MessageType
  orgId: number
  personalizationList: PersonalizationType
  selectedSenderId: string
  token: string
  sendRef: React.RefObject<HTMLDivElement>
  smsSenderIds: SenderIdType[]
  whatsappSenderIds: SenderIdType[]
  getFile: TGetFileFunction
  onMessageTypeChange: (messageType: MessageType) => void
  onMicAccessError: () => void
  onSenderIdSelect: (senderId: string) => void
  saveFile: (file: FileUploadType) => Promise<FileType | Error>
  sendWhatsappMessage: (payload: SendWhatsappMessagePayload & { token: string; orgId: number }) => Promise<unknown>
  setScrolledHistory: React.Dispatch<React.SetStateAction<boolean>>
}

export default function Send({
  contactId,
  disabled,
  messageType,
  orgId,
  personalizationList,
  selectedSenderId,
  token,
  sendRef,
  smsSenderIds,
  whatsappSenderIds,
  getFile,
  onMessageTypeChange,
  onMicAccessError,
  onSenderIdSelect,
  saveFile,
  sendWhatsappMessage,
  setScrolledHistory,
}: Props) {
  const [sendMessage] = useSendMessage()
  const [loading, setLoading] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const senderIds = messageType === MessageType.SMS ? smsSenderIds : whatsappSenderIds

  const sendSMSHandler = React.useCallback(async () => {
    setLoading(true)
    try {
      const isSuccess = await sendMessage({
        senderId: selectedSenderId === DEFAULT_SENDER_ID_VALUE ? '' : selectedSenderId,
        isAllContacts: false,
        contactIds: [contactId],
        excludeIds: [],
        message,
        includedGroupIds: [],
      })

      if (isSuccess) {
        setMessage('')
      }
    } finally {
      setLoading(false)
    }
  }, [contactId, message, selectedSenderId, sendMessage])

  const [image, setImage] = React.useState('')
  const [audio, setAudio] = React.useState('')
  const [video, setVideo] = React.useState('')
  const [document, setDocument] = React.useState('')
  const [playId, setPlaying] = React.useState('')
  const [files, setFiles] = React.useState<FilesType>({})

  const prevMessageType = usePrevious(messageType)

  React.useEffect(() => {
    if (!prevMessageType || prevMessageType === messageType) {
      return
    }
    setMessage('')
    setImage('')
    setAudio('')
    setVideo('')
    setDocument('')
    setFiles({})
  }, [messageType, prevMessageType])

  const sendWhatsappHandler = () => {
    const contactIds = [contactId]
    setLoading(true)
    sendWhatsappMessage({
      message,
      image,
      audio,
      video,
      document,
      senderId: selectedSenderId === DEFAULT_SENDER_ID_VALUE ? '' : selectedSenderId,
      isAllContacts: false,
      contactIds,
      excludeIds: [],
      includedGroupIds: [],
      search: '',
      token,
      orgId,
    })
      .then(() => {
        setLoading(false)
        setMessage('')
        setImage('')
        setAudio('')
        setVideo('')
        setDocument('')
        setFiles({})
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const sendHandler = () => {
    setScrolledHistory(false)
    if (messageType === MessageType.SMS) {
      sendSMSHandler()
    } else {
      sendWhatsappHandler()
    }
  }

  const saveFileHandler = (file: FileUploadType): Promise<void> =>
    saveFile(file).then((res: FileType | Error) => {
      if (file.cancelled) {
        return
      }
      if (res instanceof Error) {
        throw res
      }
      if (file.type === 'image') {
        setImage(res.filename)
      } else if (file.type === 'video') {
        setVideo(res.filename)
      } else if (file.type === 'document') {
        setDocument(res.filename)
      }
    })

  const saveAudioHandler = (file: FileUploadType): Promise<FileType> =>
    saveFile(file).then((res: FileType | Error) => {
      if (res instanceof Error) {
        throw res
      }
      setAudio(res.filename)
      return res
    })

  const removeMedia = () => {
    setImage('')
    setAudio('')
    setVideo('')
    setDocument('')
  }

  const handleGetFile = React.useCallback(
    (item: string) =>
      getFile(item).then((file: FileType) => {
        setFiles({ [item]: file })
        return file
      }),
    [getFile],
  )
  const handleWhatsAppChange = React.useCallback(
    ({ text: updatedText }: ContentWhatsApp) => setMessage(updatedText || ''),
    [],
  )

  const shouldConfirm = (tab: WhatsAppTab, content: ContentWhatsApp) => shouldDisplayConfirmation(tab, content, false) // Send WA message in Conversations doesn't support button or list (yet)

  return (
    <SendMessageCont ref={sendRef}>
      <SendMessageToolbar>
        {!disabled && (
          <>
            <SendMessageToolbarSelectWrapper>
              <ToggleButtonGroup
                value={messageType}
                exclusive
                onChange={(e: React.BaseSyntheticEvent, value: string) => {
                  if (value === MessageType.SMS || value === MessageType.WhatsApp) {
                    onMessageTypeChange(value)
                  }
                }}
                aria-label="text alignment"
              >
                <ToggleButton value={MessageType.SMS} aria-label="sms">
                  <Icon>sms</Icon>
                </ToggleButton>
                <ToggleButton value={MessageType.WhatsApp} aria-label="whatsapp">
                  <WhatsappIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </SendMessageToolbarSelectWrapper>
            <SendCont>
              <SendMessageToolbarSelectWrapper>
                <Select
                  color="primary"
                  variant="outlined"
                  value={selectedSenderId}
                  onChange={(e) => {
                    onSenderIdSelect(e.target.value)
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        marginTop: '-30px',
                      },
                    },
                  }}
                >
                  <MenuItem value="-none-selected-" disabled>
                    Select a Sender
                  </MenuItem>
                  {senderIds.map((senderId: SenderIdType) => (
                    <MenuItem key={senderId.value} value={senderId.value}>
                      {senderId.label}
                    </MenuItem>
                  ))}
                </Select>
              </SendMessageToolbarSelectWrapper>
              <Button
                aria-label="send message"
                size="large"
                color="primary"
                variant="contained"
                disabled={
                  loading ||
                  selectedSenderId === '-none-selected-' ||
                  !(message || audio || image || video || document) ||
                  disabled
                }
                onClick={() => sendHandler()}
              >
                {loading ? <CircularProgress style={{ width: '20px', height: '20px' }} /> : <Icon>send</Icon>}
              </Button>
            </SendCont>
          </>
        )}
      </SendMessageToolbar>
      <SendMessageInnerCont>
        {!disabled && (
          <SendMessageTextBox>
            {messageType === MessageType.WhatsApp ? (
              <WhatsApp
                audio={audio}
                canRegisterTemplate={false}
                defaultLanguage=""
                document={document}
                editable={!disabled}
                enableMediaCaption
                files={files}
                image={image}
                info=""
                label="Message"
                loading={loading}
                personalizationList={personalizationList}
                playId={playId}
                text={message}
                video={video}
                changeHandler={handleWhatsAppChange}
                getFile={handleGetFile}
                onMicAccessError={onMicAccessError}
                onRecordingChange={noop}
                removeMedia={removeMedia}
                saveFileHandler={saveAudioHandler}
                saveHandler={saveFileHandler}
                setPlaying={setPlaying}
                shouldConfirm={shouldConfirm}
              />
            ) : (
              <Message
                message={message}
                error={''}
                personalizationList={personalizationList}
                onChange={({ message: updatedMessage }: any) => {
                  setMessage(updatedMessage)
                }}
              />
            )}
          </SendMessageTextBox>
        )}
      </SendMessageInnerCont>
    </SendMessageCont>
  )
}

const SendMessageCont = styled('div')(({ theme }) => ({
  background: theme.palette.background.default,
  gridArea: 'sendMessage',
  width: '100%',
  bottom: 0,
  borderTop: `1px solid ${theme.palette.border.main}`,
}))

const SendMessageInnerCont = styled('div')({
  padding: '0px 24px 5px',
  display: 'flex',
  alignItems: 'flex-start',
  paddingRight: '92px',

  '& .send-message-info': {
    marginBottom: 0,
  },
})

const SendMessageToolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  borderBottom: `1px solid ${theme.palette.border.main}`,
  padding: '5px 24px',
  backgroundColor: theme.palette.highlight.light,
  height: '59px',
}))

const SendMessageToolbarSelectWrapper = styled('div')({
  display: 'flex',
  width: '100%',
  paddingRight: '10px',
  alignItems: 'center',
})

const SendCont = styled('div')({
  textAlign: 'center',
  display: 'flex',
})

const SendMessageTextBox = styled('div')({
  width: '100%',
  padding: '10px 10px 0 0',
})
